import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, useReducedMotion } from 'framer-motion';
import { Bell } from 'lucide-react';
import { cn } from '@shared/lib/cn';
import { useAuthStore } from '@shared/stores/authStore';
import { fetchUnreadNotificationCount } from './api';
import { NotificationsSheet } from './NotificationsSheet';

interface NotificationBellProps {
  /** Styling for the home photo header (frosted white circle). */
  variant?: 'header' | 'default';
  className?: string;
}

/**
 * Header bell — opens a swipe-up notifications sheet and shows unread count.
 * Mirrors the member portal notification menu (not support requests).
 */
export function NotificationBell({
  variant = 'default',
  className,
}: NotificationBellProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const isAuthed = useAuthStore((s) => !!(s.accessToken || s.refreshToken));
  const prefersReducedMotion = useReducedMotion();

  const unreadQuery = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: fetchUnreadNotificationCount,
    enabled: isAuthed,
    refetchInterval: isAuthed ? 30_000 : false,
  });

  const unread = unreadQuery.data ?? 0;
  const badgeLabel = unread > 99 ? '99+' : String(unread);
  const shouldShake = !prefersReducedMotion && unread > 0 && !open;

  const buttonClass =
    variant === 'header'
      ? 'flex h-9 w-9 items-center justify-center rounded-pill bg-white/15 text-white backdrop-blur'
      : 'relative flex h-10 w-10 items-center justify-center rounded-pill bg-card hairline text-ink';

  return (
    <>
      <button
        type="button"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        onClick={() => setOpen(true)}
        className={cn(buttonClass, className)}
      >
        <motion.span
          animate={
            shouldShake
              ? { rotate: [0, -18, 16, -12, 8, -4, 0], scale: [1, 1.06, 1] }
              : { rotate: 0, scale: 1 }
          }
          transition={
            shouldShake
              ? { duration: 0.72, ease: 'easeInOut', repeat: Infinity, repeatDelay: 1.6 }
              : { duration: 0.2, ease: 'easeOut' }
          }
          className="inline-flex"
        >
          <Bell className="h-4.5 w-4.5" strokeWidth={1.75} />
        </motion.span>
        {unread > 0 ? (
          <span
            className={cn(
              'absolute flex min-h-[18px] min-w-[18px] items-center justify-center rounded-pill px-1 text-[0.625rem] font-bold leading-none',
              variant === 'header'
                ? '-right-0.5 -top-0.5 bg-danger text-white'
                : '-right-1 -top-1 bg-danger text-white',
            )}
          >
            {badgeLabel}
          </span>
        ) : null}
      </button>

      <NotificationsSheet open={open} onOpenChange={setOpen} />
    </>
  );
}
